"use client"

import React from "react"
import { useState, useEffect } from "react"
import { Box, Flex, Card, Grid, Heading, Text, TextField, Button, Switch } from "@radix-ui/themes"
import { useTheme } from "../contexts/ThemeContext"
import toast from "react-hot-toast"
import PageHeader from "../components/ui/dashboard/PageHeader"
import { settingsAPI } from "../services/api"

const SettingsPage: React.FC = () => {
  const { isDarkMode, toggleTheme } = useTheme()
  const [loading, setLoading] = useState(false)
  const [settings, setSettings] = useState({
    siteName: "Turbo Delivery",
    supportEmail: "support@turbodelivery.com",
    // Delivery Fee Settings
    deliveryFeeType: "fixed" as "fixed" | "distance",
    fixedDeliveryFee: 2.99,
    baseDeliveryFee: 1.00,
    perKmRate: 0.50,
    minDeliveryFee: 1.00,
    maxDeliveryFee: 15.00,
    freeDeliveryThreshold: 50.00,
    // Other settings
    taxRate: 0.15,
    minOrderAmount: 10.0,
    maxDeliveryRadius: 15,
    enableNotifications: true,
    enableSMS: true,
    enableEmailAlerts: true,
  })

  useEffect(() => {
    fetchSettings()
  }, [])

  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.getSettings()
      if (response.data.success && response.data.data) {
        setSettings(prev => ({ ...prev, ...response.data.data }))
      }
    } catch (error) {
      console.error("Failed to fetch settings:", error)
    }
  }

  const handleSave = async () => {
    try {
      setLoading(true)
      const response = await settingsAPI.updateSettings(settings)
      if (response.data.success) {
        toast.success("Settings saved successfully!")
      }
    } catch (error) {
      console.error("Failed to save settings:", error)
      toast.error("Failed to save settings")
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (key: string, value: any) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  return (
    <Flex direction="column" gap="6">
      <PageHeader
        title="Settings"
        subtitle="Manage platform configuration"
      />

      <Grid columns={{ initial: "1", lg: "2" }} gap="6">
        {/* General Settings */}
        <Card>
          <Flex direction="column" gap="4">
            <Heading size="4">General Settings</Heading>
            <Flex direction="column" gap="4">
              <Box>
                <Text as="label" size="2" weight="medium">Site Name</Text>
                <TextField.Root
                  value={settings.siteName}
                  onChange={(e) => handleInputChange("siteName", e.target.value)}
                />
              </Box>
              <Box>
                <Text as="label" size="2" weight="medium">Support Email</Text>
                <TextField.Root
                  type="email"
                  value={settings.supportEmail}
                  onChange={(e) => handleInputChange("supportEmail", e.target.value)}
                />
              </Box>
            </Flex>
          </Flex>
        </Card>

        {/* Delivery Fee Settings */}
        <Card>
          <Flex direction="column" gap="4">
            <Heading size="4">Delivery Fee Calculation</Heading>
            <Flex direction="column" gap="4">
              <Box>
                <Text as="label" size="2" weight="medium" mb="2" style={{ display: "block" }}>Fee Type</Text>
                <Flex gap="2">
                  <Button
                    variant={settings.deliveryFeeType === "fixed" ? "solid" : "outline"}
                    onClick={() => handleInputChange("deliveryFeeType", "fixed")}
                  >
                    Fixed Fee
                  </Button>
                  <Button
                    variant={settings.deliveryFeeType === "distance" ? "solid" : "outline"}
                    onClick={() => handleInputChange("deliveryFeeType", "distance")}
                  >
                    Distance Based
                  </Button>
                </Flex>
              </Box>

              {settings.deliveryFeeType === "fixed" ? (
                <Box>
                  <Text as="label" size="2" weight="medium">Fixed Delivery Fee ($)</Text>
                  <TextField.Root
                    type="number"
                    step="0.01"
                    value={settings.fixedDeliveryFee.toString()}
                    onChange={(e) => handleInputChange("fixedDeliveryFee", Number.parseFloat(e.target.value) || 0)}
                  />
                </Box>
              ) : (
                <>
                  <Box>
                    <Text as="label" size="2" weight="medium">Base Fee ($)</Text>
                    <TextField.Root
                      type="number"
                      step="0.01"
                      value={settings.baseDeliveryFee.toString()}
                      onChange={(e) => handleInputChange("baseDeliveryFee", Number.parseFloat(e.target.value) || 0)}
                    />
                    <Text size="1" color="gray">Starting fee before distance calculation</Text>
                  </Box>
                  <Box>
                    <Text as="label" size="2" weight="medium">Rate per KM ($)</Text>
                    <TextField.Root
                      type="number"
                      step="0.01"
                      value={settings.perKmRate.toString()}
                      onChange={(e) => handleInputChange("perKmRate", Number.parseFloat(e.target.value) || 0)}
                    />
                    <Text size="1" color="gray">Additional fee per kilometer</Text>
                  </Box>
                  <Flex gap="4">
                    <Box style={{ flex: 1 }}>
                      <Text as="label" size="2" weight="medium">Min Fee ($)</Text>
                      <TextField.Root
                        type="number"
                        step="0.01"
                        value={settings.minDeliveryFee.toString()}
                        onChange={(e) => handleInputChange("minDeliveryFee", Number.parseFloat(e.target.value) || 0)}
                      />
                    </Box>
                    <Box style={{ flex: 1 }}>
                      <Text as="label" size="2" weight="medium">Max Fee ($)</Text>
                      <TextField.Root
                        type="number"
                        step="0.01"
                        value={settings.maxDeliveryFee.toString()}
                        onChange={(e) => handleInputChange("maxDeliveryFee", Number.parseFloat(e.target.value) || 0)}
                      />
                    </Box>
                  </Flex>
                </>
              )}

              <Box>
                <Text as="label" size="2" weight="medium">Free Delivery Threshold ($)</Text>
                <TextField.Root
                  type="number"
                  step="0.01"
                  value={settings.freeDeliveryThreshold.toString()}
                  onChange={(e) => handleInputChange("freeDeliveryThreshold", Number.parseFloat(e.target.value) || 0)}
                />
                <Text size="1" color="gray">Orders above this amount get free delivery (0 to disable)</Text>
              </Box>
            </Flex>
          </Flex>
        </Card>

        {/* Order Settings */}
        <Card>
          <Flex direction="column" gap="4">
            <Heading size="4">Order Settings</Heading>
            <Flex direction="column" gap="4">
              <Box>
                <Text as="label" size="2" weight="medium">Tax Rate (%)</Text>
                <TextField.Root
                  type="number"
                  step="0.01"
                  value={(settings.taxRate * 100).toString()}
                  onChange={(e) => handleInputChange("taxRate", Number.parseFloat(e.target.value) / 100)}
                />
              </Box>
              <Box>
                <Text as="label" size="2" weight="medium">Minimum Order Amount ($)</Text>
                <TextField.Root
                  type="number"
                  step="0.01"
                  value={settings.minOrderAmount.toString()}
                  onChange={(e) => handleInputChange("minOrderAmount", Number.parseFloat(e.target.value))}
                />
              </Box>
              <Box>
                <Text as="label" size="2" weight="medium">Max Delivery Radius (km)</Text>
                <TextField.Root
                  type="number"
                  value={settings.maxDeliveryRadius.toString()}
                  onChange={(e) => handleInputChange("maxDeliveryRadius", Number.parseInt(e.target.value))}
                />
              </Box>
            </Flex>
          </Flex>
        </Card>

        {/* Notification Settings */}
        <Card>
          <Flex direction="column" gap="4">
            <Heading size="4">Notification Settings</Heading>
            <Flex direction="column" gap="4">
              <Flex justify="between" align="center">
                <Text size="2" weight="medium">Push Notifications</Text>
                <Switch
                  checked={settings.enableNotifications}
                  onCheckedChange={(checked) => handleInputChange("enableNotifications", checked)}
                />
              </Flex>
              <Flex justify="between" align="center">
                <Text size="2" weight="medium">SMS Notifications</Text>
                <Switch
                  checked={settings.enableSMS}
                  onCheckedChange={(checked) => handleInputChange("enableSMS", checked)}
                />
              </Flex>
              <Flex justify="between" align="center">
                <Text size="2" weight="medium">Email Alerts</Text>
                <Switch
                  checked={settings.enableEmailAlerts}
                  onCheckedChange={(checked) => handleInputChange("enableEmailAlerts", checked)}
                />
              </Flex>
            </Flex>
          </Flex>
        </Card>

        {/* Appearance Settings */}
        <Card>
          <Flex direction="column" gap="4">
            <Heading size="4">Appearance</Heading>
            <Flex justify="between" align="center">
              <Text size="2" weight="medium">Dark Mode</Text>
              <Switch
                checked={isDarkMode}
                onCheckedChange={toggleTheme}
              />
            </Flex>
          </Flex>
        </Card>
      </Grid>

      {/* Save Button */}
      <Flex justify="end">
        <Button onClick={handleSave} size="3" disabled={loading}>
          {loading ? "Saving..." : "Save Settings"}
        </Button>
      </Flex>
    </Flex>
  )
}

export default SettingsPage
